/**
 * MessageSearch Component
 *
 * Full-text search across conversations with results display.
 * Supports debounced search, highlighting matches, and navigation
 * to specific messages in conversations.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import {
  Search,
  X,
  Loader2,
  MessageSquare,
  ArrowRight,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { messagesApi } from '@/services/api/messages';
import type { Message, Conversation } from '@/types/messaging';

interface MessageSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversations: Conversation[];
  onNavigateToMessage: (conversationId: string, messageId: string) => void;
}

interface SearchResult {
  message: Message;
  conversation?: Conversation;
}

/**
 * Get user initials
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

const getConversationHeader = (conversation?: Conversation) => {
  if (!conversation) {
    return {
      name: 'Unknown conversation',
      avatar: undefined,
      fallback: '?',
    };
  }

  if (conversation.isGroup) {
    const name = conversation.groupName?.trim() || 'Group conversation';
    return {
      name,
      avatar: conversation.groupAvatar,
      fallback: name.charAt(0).toUpperCase(),
    };
  }

  const participant = conversation.participant;
  const displayName = [participant?.firstName, participant?.lastName].filter(Boolean).join(' ');

  return {
    name: displayName || participant?.username || 'Direct conversation',
    avatar: participant?.avatar,
    fallback: getInitials(participant?.firstName, participant?.lastName, participant?.username),
  };
};

/**
 * Highlight matching text in content
 */
const highlightMatch = (text: string, query: string): React.ReactNode => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = trimmedQuery.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let matchStart = lowerText.indexOf(lowerQuery, cursor);

  while (matchStart !== -1) {
    if (cursor < matchStart) {
      parts.push(text.slice(cursor, matchStart));
    }

    const matchEnd = matchStart + trimmedQuery.length;
    const matchText = text.slice(matchStart, matchEnd);
    parts.push(
      <mark
        key={`match-${matchStart}-${matchEnd}`}
        className="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded"
      >
        {matchText}
      </mark>
    );

    cursor = matchEnd;
    matchStart = lowerText.indexOf(lowerQuery, cursor);
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
};

export const MessageSearch: React.FC<MessageSearchProps> = ({
  open,
  onOpenChange,
  conversations,
  onNavigateToMessage,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRequestIdRef = useRef(0);

  // Create conversation lookup map
  const conversationMap = useMemo(() => {
    const map = new Map<string, Conversation>();
    conversations.forEach((conv) => {
      map.set(conv.id, conv);
    });
    return map;
  }, [conversations]);

  // Debounced search
  const performSearch = useCallback(
    async (searchTerm: string, requestId: number) => {
      if (!searchTerm.trim()) {
        setResults([]);
        setIsSearching(false);
        setHasSearched(false);
        return;
      }

      setIsSearching(true);
      setHasSearched(true);

      try {
        const response = await messagesApi.searchMessages(searchTerm, { limit: 30 });
        const messages = response.data?.messages ?? [];

        if (searchRequestIdRef.current !== requestId) {
          return;
        }

        // Map messages to results with conversation info
        const searchResults: SearchResult[] = messages.map((message) => ({
          message,
          conversation: conversationMap.get(message.conversationId),
        }));

        setResults(searchResults);
      } catch (error) {
        if (searchRequestIdRef.current !== requestId) {
          return;
        }
        console.error('[MessageSearch] Search error:', error);
        setResults([]);
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [conversationMap]
  );

  // Debounce effect for search
  useEffect(() => {
    if (!open) {
      searchRequestIdRef.current += 1;
      setQuery('');
      setResults([]);
      setIsSearching(false);
      setHasSearched(false);
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;

    if (!query.trim()) {
      void performSearch(query, requestId);
      return;
    }

    const debounceTimer = setTimeout(() => {
      performSearch(query, requestId);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [open, query, performSearch]);

  // Clear search when closing
  const handleClose = useCallback(() => {
    searchRequestIdRef.current += 1;
    setQuery('');
    setResults([]);
    setIsSearching(false);
    setHasSearched(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // Handle result click
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      onNavigateToMessage(result.message.conversationId, result.message.id);
      handleClose();
    },
    [onNavigateToMessage, handleClose]
  );

  // Group results by conversation
  const groupedResults = useMemo(() => {
    const groups = new Map<string, SearchResult[]>();

    results.forEach((result) => {
      const convId = result.message.conversationId;
      const group = groups.get(convId);

      if (group) {
        group.push(result);
      } else {
        groups.set(convId, [result]);
      }
    });

    return Array.from(groups.entries());
  }, [results]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent variant="light" className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-card-foreground">
            <Search className="h-5 w-5 text-primary" />
            Search Messages
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Search across all your conversations
          </DialogDescription>
        </DialogHeader>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search messages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 pr-9"
            autoFocus
          />
          {query && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setQuery('')}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>

        {/* Results */}
        <ScrollArea className="flex-1 min-h-0 max-h-[50vh] mt-4">
          {isSearching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Start typing to search your messages</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">No messages found for "{query}"</p>
              <p className="text-xs mt-1">Try different keywords</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedResults.map(([conversationId, convResults]) => {
                const conversation = convResults[0]?.conversation;
                const header = getConversationHeader(conversation);

                return (
                  <div key={conversationId} className="space-y-2">
                    {/* Conversation header */}
                    <div className="flex items-center gap-2 px-2 py-1 bg-muted/50 rounded-md">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={header.avatar} alt={header.name} />
                        <AvatarFallback
                          className="border border-primary/20 bg-primary/10 text-xs text-primary"
                        >
                          {header.fallback}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium">{header.name}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {convResults.length} {convResults.length === 1 ? 'result' : 'results'}
                      </Badge>
                    </div>

                    {/* Messages in this conversation */}
                    <div className="space-y-1 pl-4">
                      {convResults.map((result) => (
                        <button
                          key={result.message.id}
                          onClick={() => handleResultClick(result)}
                          className="w-full text-left p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-muted-foreground mb-1">
                                {result.message.sender.firstName} {result.message.sender.lastName}
                                <span className="mx-1.5">·</span>
                                {format(new Date(result.message.createdAt), 'MMM d, yyyy h:mm a')}
                              </p>
                              <p className="text-sm line-clamp-2">
                                {highlightMatch(result.message.content, query)}
                              </p>
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {results.length > 0 && (
          <div className="pt-3 border-t text-xs text-muted-foreground text-center">
            Found {results.length} {results.length === 1 ? 'message' : 'messages'} in{' '}
            {groupedResults.length} {groupedResults.length === 1 ? 'conversation' : 'conversations'}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageSearch;
